import React, { useRef, useState, useEffect } from 'react';
import log from 'loglevel';
import { usePlayerContext } from './Context';
import Utils from '../utils/Utils';
import { StateChangedEvents } from '../sdk/PlayerEvents';
import {
  ClickAction,
  Events,
  State,
  Autoplay,
  NotificationAction,
  ControlsPosition,
} from '../types/player';
import Loading from './controls/notifications/Loading';
import Error from './controls/notifications/Error';
import Action from './controls/notifications/Action';
import BigButton from './controls/bigButton/BigButton';
import Poster from './controls/Poster';
import PluginControlContainer from './controls/PluginControlContainer';
import PluginInterface from '../sdk/plugins/PluginInterface';
import Logo from './controls/Logo';

const Inner = () => {
  const { sdk } = usePlayerContext();

  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const elementRef = useRef(null);
  const clickCountRef = useRef(0);
  const isTouch = useRef(false);

  const [playerState, setPlayerState] = useState(
    sdk.getCurrentState() as string,
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    async function init() {
      while (elementRef.current === null) {
        await Utils.sleep(100);
      }

      sdk.setContainerElement(elementRef.current);
    }

    init();

    sdk.on(Events.STATE_CHANGED, (event: StateChangedEvents) => {
      setPlayerState(event.newState);
    });

    sdk.on(Events.ERROR, () => {
      setError(true);
    });
  }, []);

  const actionClick = (action: ClickAction) => {
    if (error) return;
    switch (action) {
      case ClickAction.PLAY:
      case ClickAction.PAUSE:
        if (playerState === State.PLAYING) {
          sdk.notificationAction(NotificationAction.PAUSE);
          sdk.pause();
        } else {
          sdk.play();
          sdk.notificationAction(NotificationAction.PLAY);
        }
        break;
      case ClickAction.FULLSCREEN:
        sdk.toggleFullScreen();
        break;
      case ClickAction.CONTROLS:
        sdk.toggleControls();
        break;
    }
  };

  const handleClick = () => {
    if (error) return;
    if (isTouch.current) {
      isTouch.current = false;
      return;
    }
    handleAction();
  };

  const handleTouchEnd = () => {
    isTouch.current = true;
    handleAction();
  };

  const handleAction = () => {
    clickCountRef.current += 1;
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    clickTimeoutRef.current = setTimeout(() => {
      let action;
      if (clickCountRef.current > 1) {
        action = sdk.options().getControls().getSettings().doubleClick;
      } else {
        action = sdk.options().getControls().getSettings().click;
      }
      if (action) {
        actionClick(action);
      }
      clickCountRef.current = 0;
      clickTimeoutRef.current = null;
    }, 300);
  };

  const controlPluginFull = sdk
    .options()
    .getControls()
    .list(ControlsPosition.FULL);

  return (
    <div
      ref={elementRef}
      className={`${sdk.getClassNamePrefix()}-player-inner`}
      onClick={handleClick}
      onTouchEnd={handleTouchEnd}
      role="button"
      //tabIndex={0}
      onKeyDown={() => {}}
      style={{ backgroundColor: sdk.options().getBgColor() }}
    >
      {controlPluginFull &&
        controlPluginFull.map((plugin) => (
          <PluginControlContainer
            full={true}
            key={plugin.pluginName + '-' + plugin.controlName}
            pluginName={plugin.pluginName || ''}
            controlName={plugin.controlName || ''}
            plugin={plugin.plugin as PluginInterface}
          />
        ))}

      {(sdk.options().getControls()?.isShow() !== false ||
        sdk.options().isBackground() !== true) && (
        <>
          <Loading />
          <Error />
          <Action />
          {!error && <BigButton />}
        </>
      )}
      <Poster />
      <Logo />
    </div>
  );
};

export default Inner;
